import { useEffect, useRef, useState } from "react";
import ShowMaterialMediaByContentType from "./ShowMaterialMediaByContentType";
import { InlineMath } from "react-katex";
import {
  constructInputWithSpecialExpressionList,
  checkAnswerState,
} from "../../../utilization/CatchtivityUtilization";
import i18n from "../../../language/i18n";
import { IMatchingActivityMaterialProps } from "../../../properties/ActivityProperties";
import { matchingActivityMaterialContentStyles } from "./MatchingActivityMaterialContentStyles";
import { resolveMaterialMediaType } from "../../../utilization/StorageUtilization";
import useAnchoredMenu from "../../../hooks/useAnchoredMenu";

// Row-by-row guided pairing (design system AssignmentActivityScreenV4, aas__mp):
// each term row carries its own "Eşleşmeyi seç ▾" glass picker listing every
// option — options already used by another row show a #n badge and picking one
// MOVES it here. Graded rows recolor green/red/amber; a wrong pick OR a row left
// blank reveals the correct pair in a stacked "Doğru" chip. Prefix: mat2-.
const MatchingActivityMaterialContent = ({
  uniqueValue,
  answerMap,
  materialMap,
  contentMap,
  checkCanAnswerQuestion,
  onChange,
  isPreview,
  showCorrectAnswer,
}: IMatchingActivityMaterialProps) => {
  const [openRowKey, setOpenRowKey] = useState<string | null>(null);
  // Outside click / Escape close it, it flips up near the bottom of the card
  // and scrolls into view — without a wheel-swallowing scrim (see the hook).
  const { anchorRef, menuRef, openUp } = useAnchoredMenu<
    HTMLDivElement,
    HTMLDivElement
  >(openRowKey, () => setOpenRowKey(null));
  const [displayAnswerMap, setDisplayAnswerMap] = useState<any>(answerMap);

  // ── option order: shuffled once PER ACTIVITY (content-keyed, so a re-render
  // never silently reverts to the answer-ordered list, and a new activity on
  // the same mounted component gets a fresh shuffle) ────────────────────
  const shuffleArray = (array: any) => {
    const copyArray = JSON.parse(JSON.stringify(array));
    for (let i = copyArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copyArray[i], copyArray[j]] = [copyArray[j], copyArray[i]];
    }
    return copyArray;
  };
  const buildOptionList = () =>
    Object.keys(materialMap).map((materialKey) => materialMap[materialKey]);
  const materialMapKey = JSON.stringify(materialMap);
  const [shuffledOptionList, setShuffledOptionList] = useState<any[]>(() =>
    shuffleArray(buildOptionList()),
  );
  const shuffleKeyRef = useRef<string>(materialMapKey);
  useEffect(() => {
    if (shuffleKeyRef.current === materialMapKey) return;
    shuffleKeyRef.current = materialMapKey;
    setShuffledOptionList(shuffleArray(buildOptionList()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialMapKey]);

  // ── sync displayAnswerMap when showCorrectAnswer flips ───────────────
  useEffect(() => {
    if (showCorrectAnswer) {
      setDisplayAnswerMap(materialMap);
    } else {
      setDisplayAnswerMap(answerMap);
    }
  }, [showCorrectAnswer, answerMap, materialMap]);

  const rowKeys = Object.keys(displayAnswerMap);
  const canAnswer = checkCanAnswerQuestion();
  const isGraded = Boolean(isPreview);
  const editable = canAnswer && !isGraded && !showCorrectAnswer;

  // rowKey currently holding a value (for the #n used badge / move semantics)
  const ownerOf = (value: string): string | undefined =>
    rowKeys.find((k) => displayAnswerMap[k] === value);

  // Picking an option that another row holds MOVES it here (V4 consume).
  // The wrapper mutates the same answerMap object, so two sequential calls
  // accumulate correctly.
  const handlePick = (rowKey: string, value: string) => {
    if (!editable) return;
    const owner = ownerOf(value);
    if (owner && owner !== rowKey) {
      onChange(answerMap, owner, null);
    }
    onChange(answerMap, rowKey, value);
    setOpenRowKey(null);
  };

  const matchedCount = rowKeys.filter((k) => displayAnswerMap[k]).length;

  // ── text renderer (math / bold / underline) ──────────────────────────
  const renderText = (value: string) => (
    <span className="mat2-text">
      {constructInputWithSpecialExpressionList(value).map(
        (inputPart, index) => (
          <span
            key={index}
            className={[
              inputPart.isBold ? "mat2-text--bold" : "",
              inputPart.isUnderline ? "mat2-text--underline" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {inputPart.isEquation ? (
              <span className="mat2-math">
                <InlineMath math={inputPart.value} />
              </span>
            ) : (
              inputPart.value
            )}
          </span>
        ),
      )}
    </span>
  );

  // ── render a value (text or media) for terms / picks / menu options ──
  const renderValue = (value: string, slotIndex: number | string) => {
    if (resolveMaterialMediaType(value, contentMap.type) === "TEXT") {
      return renderText(value);
    }
    return (
      <div className="mat2-media-wrap">
        <ShowMaterialMediaByContentType
          key={`${uniqueValue}-${slotIndex}`}
          contentType={resolveMaterialMediaType(value, contentMap.type)}
          src={value}
          canFullScreen={false}
        />
      </div>
    );
  };

  return (
    <div className="mat2-root">
      <style>{matchingActivityMaterialContentStyles}</style>

      {editable ? (
        <div className="mat2-guide">
          <span className="mat2-guidetext">{i18n.t("matching_guide")}</span>
          <span className="mat2-progress">
            {i18n.t("matched_count", {
              matched: matchedCount,
              total: rowKeys.length,
            })}
          </span>
        </div>
      ) : null}

      <div className="mat2-rows">
        {rowKeys.map((rowKey, index) => {
          const chosen = displayAnswerMap[rowKey];
          // A graded row the learner never picked is BLANK — its own third
          // state, not "ungraded". It used to stay neutral, which meant a
          // skipped row showed an empty picker and NOTHING else: the one row
          // whose answer the learner does not know is the only row that never
          // reveals it. Blank now carries amber (never the red of a wrong
          // pick — they did not answer wrongly, they did not answer) and gets
          // the same "Doğru cevap" chip a wrong pick gets.
          const state = !isGraded
            ? null
            : chosen
              ? checkAnswerState(materialMap[rowKey], chosen)
              : "EMPTY";
          const isCorrect = state === "CORRECT";
          const isWrong = state === "INCORRECT" && !showCorrectAnswer;
          const isBlank = state === "EMPTY" && !showCorrectAnswer;
          const isOpen = openRowKey === rowKey && editable;

          const rowClass = [
            "mat2-row",
            chosen ? "is-set" : "",
            isOpen ? "is-open" : "",
            isCorrect ? "is-correct" : "",
            isWrong ? "is-wrong" : "",
            isBlank ? "is-blank" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div key={index} className={rowClass}>
              <span className="mat2-num">{index + 1}</span>
              <span className="mat2-term">{renderValue(rowKey, index)}</span>
              <span className="mat2-arrow" aria-hidden="true">
                →
              </span>
              <div
                className="mat2-pickwrap"
                ref={isOpen ? anchorRef : undefined}
              >
                <button
                  type="button"
                  className={`mat2-pick${chosen ? " is-filled" : ""}${
                    isCorrect ? " is-correct" : ""
                  }${isWrong ? " is-wrong" : ""}${isBlank ? " is-blank" : ""}`}
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                  onClick={() =>
                    editable && setOpenRowKey(isOpen ? null : rowKey)
                  }
                >
                  <span className="mat2-pick-val">
                    {chosen ? (
                      renderValue(chosen, `pick-${index}`)
                    ) : (
                      <span className="mat2-pick-ph">
                        {i18n.t("select_match")}
                      </span>
                    )}
                  </span>
                  {editable ? (
                    <span className="mat2-caret" aria-hidden="true">
                      ▾
                    </span>
                  ) : null}
                  {isCorrect ? <span className="mat2-ic">✓</span> : null}
                  {isWrong ? <span className="mat2-ic">✕</span> : null}
                  {isBlank ? <span className="mat2-ic">—</span> : null}
                </button>

                {isOpen ? (
                  <div
                    ref={menuRef}
                    className={`mat2-menu${openUp ? " is-up" : ""}`}
                    role="listbox"
                  >
                    {shuffledOptionList.map((option, optionIndex) => {
                      const owner = ownerOf(option);
                      const usedElsewhere = owner != null && owner !== rowKey;
                      const isThis = chosen === option;
                      return (
                        <button
                          key={optionIndex}
                          type="button"
                          role="option"
                          aria-selected={isThis}
                          className={`mat2-opt${isThis ? " is-on" : ""}`}
                          onClick={() => handlePick(rowKey, option)}
                        >
                          <span className="mat2-opt-text">
                            {renderValue(option, `opt-${optionIndex}`)}
                          </span>
                          {usedElsewhere ? (
                            <span className="mat2-opt-used">
                              #{rowKeys.indexOf(owner as string) + 1}
                            </span>
                          ) : null}
                          {isThis ? (
                            <span className="mat2-opt-check">✓</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}

                {isWrong || isBlank ? (
                  <span className="mat2-corr">
                    <span className="mat2-corr-tag">
                      {i18n.t("correct_label")}
                    </span>
                    <span className="mat2-corr-val">
                      {renderValue(materialMap[rowKey], `corr-${index}`)}
                    </span>
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MatchingActivityMaterialContent;
